/** @jsxImportSource @emotion/react */
import {
  List,
  ListItem,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import {
  CaseItem,
  CaseState,
  useBatchUpdateCaseState,
  useBatchUpdateReportedToFipo,
} from 'api/useCasesApi';

interface Props {
  cases: CaseItem[];
  onComplete: () => void;
  onDeleteClick: () => void;
}

export const BatchActions = ({ cases, onComplete, onDeleteClick }: Props) => {
  const { spacing } = useTheme();

  const batchUpdateStateMutation = useBatchUpdateCaseState();
  const batchReportedToFipoMutation = useBatchUpdateReportedToFipo();

  const allInState = (state: CaseState) =>
    cases.every((c) => c.state === state);

  const allReportedToFipo = cases.every((c) => c.reportedToFipo);
  const noneReportedToFipo = cases.every((c) => !c.reportedToFipo);

  const caseIds = cases.map((c) => c.uuid);

  const displayResult = (result: Record<string, boolean> | undefined) => {
    if (!result) return '';
    const entries = Object.entries(result);
    if (entries.length === 1) {
      return entries[0][1] ? 'Success' : 'Failed';
    }
    const successCount = entries.filter(([, success]) => success).length;
    return `${successCount}/${entries.length} succeeded`;
  };

  const handleCloseCase = () => {
    batchUpdateStateMutation.mutate(
      { caseIds, state: CaseState.CLOSED },
      { onSuccess: onComplete }
    );
  };

  const handleReopenCase = () => {
    batchUpdateStateMutation.mutate(
      { caseIds, state: CaseState.ACTIVE },
      { onSuccess: onComplete }
    );
  };

  const handleMarkReportedToFipo = () => {
    batchReportedToFipoMutation.mutate(
      { caseIds, reportedToFipo: true },
      { onSuccess: onComplete }
    );
  };

  const handleUnmarkReportedToFipo = () => {
    batchReportedToFipoMutation.mutate(
      { caseIds, reportedToFipo: false },
      { onSuccess: onComplete }
    );
  };

  return (
    <div css={{ padding: spacing.xxs }}>
      <Typography variant="bodyBold">
        Batch Actions ({cases.length} selected)
      </Typography>
      <List>
        {!allInState(CaseState.CLOSED) && (
          <ListItem
            icon={
              !batchUpdateStateMutation.data ? (
                <UtilityButton
                  text="Close All"
                  loading={batchUpdateStateMutation.isPending}
                  disabled={batchUpdateStateMutation.isPending}
                  onClick={handleCloseCase}
                />
              ) : undefined
            }
            title={displayResult(batchUpdateStateMutation.data)}
          />
        )}
        {!allInState(CaseState.ACTIVE) && (
          <ListItem
            icon={
              !batchUpdateStateMutation.data ? (
                <UtilityButton
                  text="Reopen All"
                  loading={batchUpdateStateMutation.isPending}
                  disabled={batchUpdateStateMutation.isPending}
                  onClick={handleReopenCase}
                />
              ) : undefined
            }
            title={displayResult(batchUpdateStateMutation.data)}
          />
        )}
        {!allReportedToFipo && (
          <ListItem
            icon={
              !batchReportedToFipoMutation.data ? (
                <UtilityButton
                  text="Mark Reported to FIPO"
                  loading={batchReportedToFipoMutation.isPending}
                  disabled={batchReportedToFipoMutation.isPending}
                  onClick={handleMarkReportedToFipo}
                />
              ) : undefined
            }
            title={displayResult(batchReportedToFipoMutation.data)}
          />
        )}
        {!noneReportedToFipo && (
          <ListItem
            icon={
              !batchReportedToFipoMutation.data ? (
                <UtilityButton
                  text="Unmark Reported to FIPO"
                  loading={batchReportedToFipoMutation.isPending}
                  disabled={batchReportedToFipoMutation.isPending}
                  onClick={handleUnmarkReportedToFipo}
                />
              ) : undefined
            }
            title={displayResult(batchReportedToFipoMutation.data)}
          />
        )}
        <ListItem
          icon={
            <UtilityButton
              text="Delete All"
              variant="secondary"
              onClick={onDeleteClick}
            />
          }
          title=""
        />
      </List>
    </div>
  );
};
